/**
 * CrewLink Audio Utility - PRD-049
 *
 * Provides notification sounds for CrewLink events using Web Audio API.
 * No external audio files needed - all sounds generated programmatically.
 */

// Singleton audio context (created on first user interaction)
let audioContext = null;

// Initialize audio context (must be called after user interaction)
const getAudioContext = () => {
    if (!audioContext) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
            audioContext = new AudioContext();
        }
    }

    // Resume if suspended (browser autoplay policy)
    if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume();
    }

    return audioContext;
};

// Play a single beep with given parameters
const playTone = (ctx, frequency, startTime, duration, volume = 0.15, type = 'sine') => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    // Envelope for smooth sound
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
    gainNode.gain.linearRampToValueAtTime(volume * 0.8, startTime + duration * 0.7);
    gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration);
};

/**
 * Sound types available for CrewLink notifications
 */
export const SoundType = {
    USER_JOINED: 'user_joined',      // When someone joins a session
    NEW_MESSAGE: 'new_message',       // New chat message
    TRANSMISSION: 'transmission',     // Incoming transmission (join request)
    SOS_ALERT: 'sos_alert',          // SOS/emergency alert
    SUCCESS: 'success',              // Success confirmation
    ERROR: 'error'                   // Error notification
};

/**
 * Play a notification sound
 * @param {string} soundType - Type of sound from SoundType enum
 * @param {number} volume - Volume level (0.0 to 1.0), default 0.15
 */
export const playNotificationSound = (soundType, volume = 0.15) => {
    try {
        console.log('[CrewLinkAudio] Playing sound:', soundType);
        const ctx = getAudioContext();
        if (!ctx) {
            console.warn('[CrewLinkAudio] No audio context available - user interaction may be required');
            return;
        }
        console.log('[CrewLinkAudio] Audio context state:', ctx.state);

        const now = ctx.currentTime;

        switch (soundType) {
            case SoundType.USER_JOINED:
                // Rising two-tone chime - welcoming sound
                playTone(ctx, 523, now, 0.15, volume);           // C5
                playTone(ctx, 659, now + 0.12, 0.2, volume);     // E5
                playTone(ctx, 784, now + 0.24, 0.25, volume);    // G5
                break;

            case SoundType.NEW_MESSAGE:
                // Quick double beep - attention-getting but not intrusive
                playTone(ctx, 880, now, 0.08, volume * 0.8);     // A5
                playTone(ctx, 880, now + 0.12, 0.08, volume * 0.8); // A5
                break;

            case SoundType.TRANSMISSION:
                // Sci-fi transmission beep sequence
                playTone(ctx, 880, now, 0.15, volume);           // A5
                playTone(ctx, 1100, now + 0.18, 0.15, volume);   // C#6
                playTone(ctx, 1320, now + 0.36, 0.25, volume);   // E6
                break;

            case SoundType.SOS_ALERT:
                // Urgent pulsing alarm
                for (let i = 0; i < 3; i++) {
                    playTone(ctx, 440, now + (i * 0.3), 0.12, volume, 'square');  // A4
                    playTone(ctx, 880, now + 0.15 + (i * 0.3), 0.12, volume, 'square'); // A5
                }
                break;

            case SoundType.SUCCESS:
                // Ascending arpeggio
                playTone(ctx, 523, now, 0.1, volume);            // C5
                playTone(ctx, 659, now + 0.1, 0.1, volume);      // E5
                playTone(ctx, 784, now + 0.2, 0.15, volume);     // G5
                playTone(ctx, 1047, now + 0.3, 0.2, volume);     // C6
                break;

            case SoundType.ERROR:
                // Descending two-tone
                playTone(ctx, 440, now, 0.2, volume, 'triangle'); // A4
                playTone(ctx, 330, now + 0.2, 0.25, volume, 'triangle'); // E4
                break;

            default:
                // Default notification beep
                playTone(ctx, 880, now, 0.15, volume);
        }
    } catch (err) {
        console.log('[CrewLinkAudio] Error playing sound:', err);
    }
};

/**
 * Pre-initialize audio context on user interaction
 * Call this on first user click to enable sounds
 */
export const initAudio = () => {
    getAudioContext();
};

const crewlinkAudio = {
    SoundType,
    playNotificationSound,
    initAudio
};

export default crewlinkAudio;
